import React, { useState, useRef } from 'react';
import * as styles from './styles.module.css';
import Modal from '../Modal';

import {
  MediaControlBar,
  MediaController,
  MediaMuteButton,
  MediaVolumeRange,
  MediaPlayButton,
  MediaTimeDisplay,
  MediaTimeRange,
} from 'media-chrome/react';
import { TbPlayerTrackNextFilled, TbPlayerTrackPrevFilled } from 'react-icons/tb';
import { RiPlayListFill } from 'react-icons/ri';
import { FaPlay } from 'react-icons/fa6';
import { useIsMobile } from '@src/hooks/useBreakpoints';

type AudioPlayerProps = {
  audioFiles: {
    fileUrl: string;
    title: string;
  }[];
};

const AudioPlayer = ({ audioFiles }: AudioPlayerProps) => {
  const [currentAudioIndex, setCurrentAudioIndex] = useState(0);
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showPlaylist, setShowPlaylist] = useState(false);

  const handleAudioEnd = async () => {
    setCurrentAudioIndex((prevIndex) => (prevIndex + 1) % audioFiles.length);

    await handleAudioPlay();
  };

  const handleAudioPlay = async () => {
    if (audioRef.current) {
      audioRef.current.load();

      await new Promise((resolve) => {
        audioRef.current!.addEventListener('canplay', resolve, { once: true });
      });

      audioRef.current.play();

      setIsPlaying(true);
    }
  };

  const handleNext = async () => {
    setCurrentAudioIndex((prevIndex) => (prevIndex + 1) % audioFiles.length);
    await handleAudioPlay();
  };

  const handlePrevious = async () => {
    setCurrentAudioIndex((prevIndex) => (prevIndex - 1 + audioFiles.length) % audioFiles.length);
    await handleAudioPlay();
  };

  const handlePlaylistItemClick = async (index: number) => {
    setCurrentAudioIndex(index);
    setShowPlaylist(false);
    await handleAudioPlay();
  };

  const isMobile = useIsMobile();

  return (
    <div className={styles.audioPlayerWrapper}>
      <MediaController audio>
        <audio
          ref={audioRef}
          slot="media"
          src={`https://mymathscloud.ams3.digitaloceanspaces.com/Audio/${audioFiles[currentAudioIndex].fileUrl}`}
          onEnded={handleAudioEnd}
        />
        <MediaControlBar>
          <MediaPlayButton onClick={() => setIsPlaying(!isPlaying)} />
          <MediaTimeDisplay />
          {!isMobile && <MediaTimeRange />}
          <MediaMuteButton />
          <button onClick={handlePrevious}>
            <TbPlayerTrackPrevFilled />
          </button>
          <button onClick={handleNext}>
            <TbPlayerTrackNextFilled />
          </button>
          <button onClick={() => setShowPlaylist(true)}>
            <RiPlayListFill />
          </button>
          <MediaVolumeRange />
        </MediaControlBar>
      </MediaController>
      {isPlaying && (
        <div className={styles.controlBar}>
          <span>Playing {audioFiles[currentAudioIndex].title}</span>
        </div>
      )}

      {showPlaylist && (
        <Modal title="Relaxation Song Playlist" onClose={() => setShowPlaylist(false)}>
          <div className={styles.playlistContainer}>
            {audioFiles.map((audio, index) => (
              <div
                key={index}
                className={`${styles.playlistItem} ${
                  index === currentAudioIndex ? styles.currentTrack : ''
                }`}
                onClick={() => handlePlaylistItemClick(index)}
              >
                <div className={styles.trackInfo}>
                  <span className={styles.trackNumber}>{index + 1}</span>
                  <span className={styles.trackTitle}>{audio.title}</span>
                </div>
                {index === currentAudioIndex && isPlaying && (
                  <span className={styles.playingIndicator}>
                    <FaPlay />
                  </span>
                )}
              </div>
            ))}
          </div>
        </Modal>
      )}
    </div>
  );
};

export default AudioPlayer;
